/* eslint-disable camelcase */
/* eslint-disable import/no-named-as-default-member */
import { batch } from 'react-redux';
import i18next from 'i18next';
import { createSelector } from 'reselect';
import * as R from 'ramda';

import * as API from 'services/ErplyAPI/api';
import * as AccountAPI from 'services/AccountAdmin';
import { proxy } from 'services/shared';
import { resetDB } from 'services/DB';
import { ErplyApiError } from 'services/ErplyAPI/core/apiErrors';
import {
  GET_COMAPNY_INFO,
  TYPE_EDIT_EMPLOYEE_DATA,
  TYPE_LOGIN,
  TYPE_LOGIN_OFFLINE,
  TYPE_LOGOUT,
  TYPE_LOGOUT_ERROR,
  TYPE_LOGOUT_OFFLINE,
  TYPE_LOGOUT_SOFT,
  TYPE_LOGOUT_SOFT_ERROR,
  TYPE_LOGOUT_SOFT_START,
  TYPE_LOGOUT_START,
  TYPE_SETLOADING,
  TYPE_SETUSERRIGHTS,
  TYPE_SET_USER_SALES_DATA,
  TYPE_SWITCH_USER,
} from 'constants/Login';
import {
  REDUX_CLIENTCODE,
  REDUX_CUSTOMER_REGISTRY_TOKEN,
  REDUX_CUSTOMER_REGISTRY_URL,
  REDUX_OFFLINELOGOUTPIN,
  REDUX_PASSWORD,
  REDUX_PIN,
  REDUX_POS,
  REDUX_POSID,
  REDUX_SERVICE_ENDPOINTS,
  REDUX_SESSIONKEY,
  REDUX_SETTINGS,
  REDUX_USER,
  REDUX_USERNAME,
  REDUX_WAREHOUSE,
  REDUX_WAREHOUSEID,
  REDUX_JWT,
} from 'constants/persistence';
import {
  getClientCode,
  getSessionKey,
  getUserLoggedIn,
  getUsername,
} from 'reducers/Login';
import { loadPointsOfSale, selectPos } from 'actions/PointsOfSale';
import { getWarehouses } from 'actions/warehouses';
import { setSettings } from 'actions/configs';
import { erplyAlertOnError } from 'store/middleware/erplyAlertIntegration';
import { addError } from 'actions/Error';
import { getAllowOfflineLogin, getSetting } from 'reducers/configs/settings';
import { offlineLogin } from 'services/localDB';
import { SO } from 'services/DB/types';
import { urlEncode } from 'utils';
import { getEndpointForService } from 'reducers/configs/serviceEnpoints';
import { getPluginLifecycleHook } from 'reducers/Plugins';
import { getConnectionHealth } from 'reducers/connectivity/connection';
import { createConfirmation } from 'actions/Confirmation';
import { ErplyApiUser } from 'types/User';
import {
  GetUserRightsParams,
  GetUserRightsResponse,
  ModuleKey,
} from 'services/ErplyAPI/core/types';
import { UserRight } from 'services/AccountAdmin/types';
import { posSendMessage } from 'utils/hooks/useWrapper';
import { ObfuscatedLocalStorage } from 'utils/ObfuscatedLocalStorage';
import { fetchCafaConfigPerLevel } from 'actions/integrations/CafaConfigs';
import { getSelectedWarehouseID } from 'reducers/warehouses';

export const setLogin = ({
  user,
  username,
  clientCode,
  pin,
  password,
}: {
  user: ErplyApiUser;
  username: string;
  clientCode: string;
  pin?: string;
  password?: string;
}) => ({
  type: TYPE_LOGIN,
  payload: {
    user,
    username,
    clientCode,
    pin,
    password,
  },
});

export const editEmployee = ({
  user,
  username,
  clientCode,
  password,
}: {
  user: ErplyApiUser;
  username: string;
  clientCode: string;
  password?: string;
}) => ({
  type: TYPE_EDIT_EMPLOYEE_DATA,
  payload: {
    user,
    username,
    clientCode,
    password,
  },
});

export const setUserSalesData = payload => ({
  type: TYPE_SET_USER_SALES_DATA,
  payload,
});

const setLoading = v => ({
  type: TYPE_SETLOADING,
  payload: v,
});

const setUserRights = v => ({
  type: TYPE_SETUSERRIGHTS,
  payload: v,
});

const switchUser = v => ({
  type: TYPE_SWITCH_USER,
  payload: v,
});

/**
 * Attempt to log in with the given cc/uname/pass combination
 * If successful, save the returned login/session credentials in redux and load user rights, configs, points of sale, etc.
 *
 * Resolves only when all the fetches are complete
 */
export const attemptLogin = (
  clientCode: string,
  username: string,
  password: string,
) =>
  erplyAlertOnError('login', async (dispatch, getState) => {
    dispatch(setLoading(true));
    try {
      const results = await API.loginWithPass({
        clientCode,
        username,
        password,
      });
      ObfuscatedLocalStorage.setItem(
        REDUX_CLIENTCODE,
        JSON.stringify(clientCode),
      );
      ObfuscatedLocalStorage.setItem(REDUX_PASSWORD, JSON.stringify(password));

      if (results[0].customerRegistryURLs.length) {
        localStorage.setItem(
          REDUX_CUSTOMER_REGISTRY_URL,
          results[0].customerRegistryURLs[0].url,
        );
        localStorage.setItem(
          REDUX_CUSTOMER_REGISTRY_TOKEN,
          results[0].customerRegistryURLs[0].token.toString(),
        );
      } else {
        localStorage.removeItem(REDUX_CUSTOMER_REGISTRY_URL);
        localStorage.removeItem(REDUX_CUSTOMER_REGISTRY_TOKEN);
      }
      posSendMessage('auth:login')();

      ObfuscatedLocalStorage.setItem(REDUX_JWT, results[0].token);

      dispatch(setLogin({ user: results[0], username, clientCode, password }));
      await dispatch([
        getUserRights(),
        setSettings(),
        // setObjects(), // TODO: This hangs for some accounts maybe
        loadPointsOfSale(),
        getWarehouses(),
      ]);
      offlineLogin.setUser(clientCode, results[0], password);
      return true;
    } catch (e) {
      if (e instanceof ErplyApiError) {
        switch (e.errorCode) {
          case 1051:
            e.message = i18next.t('validation:ErplyAPI.error.credentials');
            throw e;
          default:
          // Do nothing
        }
      }

      const allowed = getAllowOfflineLogin(getState());
      if (!allowed) {
        throw e;
      }
      const offlineLoginUser =
        allowed &&
        (await offlineLogin.getUser(
          clientCode,
          username.toLowerCase(),
          password,
        ));
      if (!offlineLoginUser) {
        throw new Error(i18next.t('validation:ErplyAPI.error.credentials'));
      }
      dispatch(
        setLogin({
          user: offlineLoginUser,
          username,
          clientCode,
          password,
        }),
      );
      await dispatch([
        getUserRights(),
        setSettings(),
        // setObjects(), // TODO: This hangs for some accounts maybe
        loadPointsOfSale(),
        getWarehouses(),
      ]);
    } finally {
      dispatch(setLoading(false));
    }
  });

export const attemptCookieLogin = (clientCode: string) => async dispatch => {
  dispatch(setLoading(true));
  try {
    const response = await window
      .fetch(`https://${clientCode}.erply.com/api/`, {
        credentials: 'include', // same-origin won't work in localhost
        // maybe won't work in production either
        method: 'POST',
        headers: { 'content-type': 'application/x-www-form-urlencoded' },
        body: urlEncode({
          request: 'getSessionKeyUser',
          clientCode,
          partnerKey: process.env.REACT_APP_PARTNER_KEY,
        }),
      })
      .then(res => res.json())
      .then(res => res.records[0]);
    ObfuscatedLocalStorage.setItem(
      REDUX_CLIENTCODE,
      JSON.stringify(clientCode),
    );
    ObfuscatedLocalStorage.setItem(
      REDUX_SESSIONKEY,
      JSON.stringify(response.sessionKey),
    );
    ObfuscatedLocalStorage.setItem(REDUX_USER, JSON.stringify(response));

    if (response.customerRegistryURLs.length) {
      localStorage.setItem(
        REDUX_CUSTOMER_REGISTRY_URL,
        response.customerRegistryURLs[0].url,
      );
      localStorage.setItem(
        REDUX_CUSTOMER_REGISTRY_TOKEN,
        response.customerRegistryURLs[0].token,
      );
    } else {
      localStorage.removeItem(REDUX_CUSTOMER_REGISTRY_URL);
      localStorage.removeItem(REDUX_CUSTOMER_REGISTRY_TOKEN);
    }

    posSendMessage('auth:login')();

    dispatch(
      setLogin({
        user: response,
        username: response.username,
        clientCode,
        password: undefined,
      }),
    );
    await dispatch([
      getUserRights(),
      setSettings(),
      // setObjects(), // TODO: This hangs for some accounts maybe
      loadPointsOfSale(),
      getWarehouses(),
    ]);
    offlineLogin.setUser(clientCode, response, undefined);
  } catch (e) {
    if (e instanceof ErplyApiError) {
      switch (e.errorCode) {
        case 1051:
          e.message = i18next.t('validation:ErplyAPI.error.credentials');
          throw e;
        default:
        // Do nothing
      }
    }
    throw e;
  } finally {
    dispatch(setLoading(false));
  }
};

/**
 * Fetch company info and save it in redux
 * Resolves when done fetching
 */
export const getCompanyInfo = () => async dispatch => {
  dispatch({ type: GET_COMAPNY_INFO.START });
  try {
    const [company] = await API.getCompanyInfo();
    dispatch({ type: GET_COMAPNY_INFO.SUCCESS, payload: company });
  } catch (err) {
    console.error('Failed to load company info', err);
  }
};

const getShowOfflinePrompts = createSelector(
  state => getConnectionHealth(state),
  state => getSetting('touchpos_allow_offline_login', '0')(state),
  (health, allowOffline) => String(allowOffline) === '1' && !health,
);
const translate = i18next.getFixedT(null, 'offlineLogin');
const showOfflineLogoutPrompt = async dispatch =>
  new Promise((resolve, reject) => {
    dispatch(
      createConfirmation(resolve, reject, {
        title: translate('onLogout.title'),
        body: `${translate('onLogout.description')}\n\n${translate(
          'onLogout.question',
        )}`,
        confirmText: translate('onLogout.buttons.ok'),
        cancelText: translate('onLogout.buttons.cancel'),
      }),
    );
  });
const showOfflineLockPrompt = async dispatch =>
  new Promise((resolve, reject) => {
    dispatch(
      createConfirmation(resolve, reject, {
        title: translate('onLock.title'),
        body: translate('onLock.description'),
        inputs: [
          {
            name: 'offlineLoginPin',
            type: 'password',
            title: translate('onLock.fields.pin'),
          },
        ],
      }),
    );
  });
/**
 * Log out and erase all session data from SSO
 * This is the only secure form of logout
 */
export const hardLogout = () => async (dispatch, getState) => {
  const { before, on, after } = getPluginLifecycleHook('onHardLogout')(
    getState(),
  );
  try {
    if (getShowOfflinePrompts(getState())) {
      const userConfirmed = await dispatch(showOfflineLogoutPrompt).then(
        () => true,
        () => false,
      );
      if (!userConfirmed) {
        return;
      }
    }
    await dispatch(before({}));
    dispatch({ type: TYPE_LOGOUT_START });
    // Disable sessionkey
    const clientCode = getClientCode(getState());
    const endpoint = getEndpointForService('auth')?.url;
    if (endpoint) {
      const sk = getSessionKey(getState());
      await window.fetch(`${proxy}${endpoint}v1/session`, {
        method: 'DELETE',
        headers: { clientCode, sessionKey: sk },
      });
    }
    // await sso.endSession();
    localStorage.removeItem(REDUX_POSID);
    localStorage.removeItem(REDUX_POS);
    ObfuscatedLocalStorage.removeItem(REDUX_USER);
    localStorage.removeItem(REDUX_WAREHOUSEID);
    localStorage.removeItem(REDUX_WAREHOUSE);
    // localStorage.removeItem(REDUX_CLIENTCODE); // Note: PBIB-1922 maintain clientcode on logout
    ObfuscatedLocalStorage.removeItem(REDUX_USERNAME);
    localStorage.removeItem(REDUX_SETTINGS);
    ObfuscatedLocalStorage.removeItem(REDUX_SESSIONKEY);
    ObfuscatedLocalStorage.removeItem(REDUX_PASSWORD);
    ObfuscatedLocalStorage.removeItem(REDUX_PIN);
    localStorage.removeItem(REDUX_OFFLINELOGOUTPIN);
    localStorage.removeItem('lastTimestamp');
    localStorage.removeItem(REDUX_SERVICE_ENDPOINTS);
    posSendMessage('auth:hard-logout')();
    if (clientCode) {
      await resetDB(clientCode, SO.SHARED.NAME);
    }
    await dispatch(on({}, {}));
    // alert('login.js:220');
    // console.error('window.location.href = `${LOGIN_URL}login/erply?redirectUrl=${window.origin}&disableAutoLogin=1`;')
    // window.location.href = `${LOGIN_URL}login/erply?redirectUrl=${window.origin}&disableAutoLogin=1`;
    dispatch({ type: TYPE_LOGOUT });
    dispatch(selectPos(null));
    await dispatch(after({}, null));
  } catch (e) {
    const err = e as Error;
    dispatch(addError(err.message));
    dispatch({ type: TYPE_LOGOUT_ERROR });
  }
};

/**
 * Pretend to log out, but keep the sessionKey in SSO
 */
export const softLogout = ({
  resetPOS = true,
  path = '/erply',
}: { resetPOS?: boolean; path?: string } = {}) => async (
  dispatch,
  getState,
) => {
  const { before, on, after } = getPluginLifecycleHook('onSoftLogout')(
    getState(),
  );
  if (getShowOfflinePrompts(getState())) {
    return dispatch(showOfflineLockPrompt).then(
      res =>
        dispatch({
          type: TYPE_LOGOUT_OFFLINE,
          payload: { pin: res.data.offlineLoginPin ?? '' },
        }),
      // User cancelled locking of the POS
      e => undefined,
    );
  }
  try {
    await dispatch(before({ resetPOS, path }));
    dispatch({ type: TYPE_LOGOUT_SOFT_START });
    if (resetPOS) {
      localStorage.removeItem(REDUX_WAREHOUSEID);
      localStorage.removeItem(REDUX_POSID);
      localStorage.removeItem(REDUX_POS);
      localStorage.removeItem(REDUX_WAREHOUSE);
    }
    // localStorage.removeItem(REDUX_USER);
    // localStorage.removeItem(REDUX_CLIENTCODE);
    ObfuscatedLocalStorage.removeItem(REDUX_USERNAME);
    localStorage.removeItem(REDUX_SETTINGS);
    // localStorage.removeItem(REDUX_SESSIONKEY);
    ObfuscatedLocalStorage.removeItem(REDUX_PASSWORD);
    ObfuscatedLocalStorage.removeItem(REDUX_PIN);
    // alert('login.js:251')
    // console.error('window.location.href = `${LOGIN_URL}login/erply?redirectUrl=${window.origin}&disableAutoLogin=1`;');
    // window.location.href = `${LOGIN_URL}login${path}?redirectUrl=${window.origin}&disableAutoLogin=1`;
    localStorage.removeItem('lastTimestamp');
    posSendMessage('auth:soft-logout')();
    await dispatch(on({ resetPOS, path }, {}));
    dispatch({ type: TYPE_LOGOUT_SOFT });
    if (resetPOS) {
      dispatch(selectPos(null));
    }
    await dispatch(after({ resetPOS, path }, null));
  } catch (e) {
    const err = e as Error;
    dispatch(addError(err.message));
    dispatch({ type: TYPE_LOGOUT_SOFT_ERROR });
    console.error('Error when locking screen', err);
  }
};

/**
 * Attempts to log in the user with a pin code
 * This only works if the user has already logged in and is only softly logged out
 * @param pin the pin code to attempt login with
 */
export const attemptPinLogin = pin =>
  erplyAlertOnError('login', async (dispatch, getState) => {
    const clientCode = getClientCode(getState());
    const sessionKey = getSessionKey(getState());
    const username = getUsername(getState());
    dispatch(setLoading(true));
    try {
      dispatch({ type: TYPE_LOGIN_OFFLINE, payload: { pin } });
      posSendMessage('auth:login')();
      if (getUserLoggedIn(getState())) {
        return true;
      }

      const results = await API.loginWithPin({
        clientCode,
        sessionKey,
        cardCode: pin,
      });
      dispatch(
        setLogin({
          user: results[0],
          pin,
          clientCode,
          username: results[0]?.userName ?? username,
        }),
      );
      await dispatch([
        getUserRights(),
        setSettings(),
        // setObjects(), // TODO: This hangs for some accounts maybe
        loadPointsOfSale(),
        getWarehouses(),
      ]);
      return true;
    } catch (e) {
      if (e instanceof ErplyApiError) {
        switch (e.errorCode) {
          case 1010:
            e.message = i18next.t('validation:ErplyAPI.error.pin.missing');
            break;
          case 1051:
            e.message = i18next.t('validation:ErplyAPI.error.pin.invalid');
            break;
          default:
          // Do nothing
        }
      }
      throw e;
    } finally {
      dispatch(setLoading(false));
    }
  });

/** Attempt to log in to the demo user */
export const attemptDemoLogin = (country, email) =>
  erplyAlertOnError('login', async dispatch => {
    dispatch(setLoading(true));
    try {
      const data = await API.createDemoUser({ country, email });
      const { clientCode, username, password } = data[0];
      await dispatch(attemptLogin(clientCode, username, password));
      await dispatch([getUserRights(), loadPointsOfSale()]);
    } catch (e) {
      throw e;
    } finally {
      dispatch(setLoading(false));
    }
  });

/** Attempt to register and log in with a new user */
export const attemptSignup = (
  countryCode,
  email,
  firstName,
  lastName,
  companyName,
  phone,
  website,
  password,
  nrStores,
) =>
  erplyAlertOnError('login', async dispatch => {
    try {
      dispatch(setLoading(true));
      const data = await API.signUp({
        countryCode,
        email,
        firstName,
        lastName,
        companyName,
        phone,
        website,
        password,
        nrStores,
      });
      const { clientCode, username, password: passwd } = data[0];
      batch(async () => {
        await dispatch(attemptLogin(clientCode, username, passwd));
        return dispatch([loadPointsOfSale(), getWarehouses()]);
      });
    } finally {
      dispatch(setLoading(false));
    }
  });

const moduleWithFallback = (
  mod: UserRight,
  fallbackModule: GetUserRightsResponse['modules'][ModuleKey],
) => {
  const { edit, add, view, delete: del, sectionName, ...rest } = mod;
  return {
    edit: edit ?? fallbackModule.edit,
    add: add ?? fallbackModule.add,
    view: view ?? fallbackModule.view,
    delete: del ?? fallbackModule.delete,
    ...rest,
  };
};

async function fetchUserRightsFromApis(
  params: GetUserRightsParams = {},
  session?: { clientCode: string; sessionKey: string },
) {
  const [userRights] = await API.getUserRights(params);
  const userRightModules = await AccountAPI.getUserRights(session).catch(
    err => {
      console.log('Failed to fetch Account Admin user rights: ', err);
      return [];
    },
  );
  // temporary workaround; fixes issue where AccountAdmin API and Erply API return different user permissions for POS user
  userRightModules.forEach(module => {
    userRights.modules[module.sectionName] = R.mergeDeepRight(
      userRights.modules[module.sectionName],
      moduleWithFallback(module, userRights.modules[module.sectionName]),
    );
  });
  return userRights;
}
/** Fetch the user rights from Erply API and set them to Redux
 * @param {Object} params - Parameters that will be passed to API request
 * @param {(number|undefined)} params.userID
 */
export const fetchUserRightsFromAPIsAndSetToState = (
  params: GetUserRightsParams = {},
) => async dispatch => {
  const finalUserRights = await fetchUserRightsFromApis(params);
  dispatch(setUserRights(finalUserRights));
  return finalUserRights;
};

/** Fetch the user rights from Erply API and display a prompt on failure
 * @param {Object} params - Parameters that will be passed to API request
 * @param {(number|undefined)} params.userID
 */
export function getUserRights(params: GetUserRightsParams = {}) {
  return async dispatch => {
    try {
      await dispatch(fetchUserRightsFromAPIsAndSetToState(params));
    } catch (e) {
      return dispatch(
        createConfirmation(
          () => {
            // Calls the same action recursively until it succeeds
            return dispatch(getUserRights(params));
          },
          null,
          {
            title: i18next.t('alerts:errors.failedToFetchUserRights', {
              context: 'title',
            }),
            body: i18next.t('alerts:errors.failedToFetchUserRights', {
              context: 'body',
            }),
          },
        ),
      );
    }
  };
}

export const getRightsForUserID = (userID?: number) =>
  API.getUserRights({ userID }).then(records => records[0]);

export const fetchSalesData = dispatch =>
  API.getEmployeesStats().then(data => dispatch(setUserSalesData(data?.[0])));

/**
 * Given credentials, switches the POS over to the new user.
 * Updates any user-specific data, such as sales statistics, permissions, or user-level CAFA records.
 *
 * Keeps the POS logged in the whole time, to avoid triggering new data fetches or flashing the login UI on screen.
 *
 * NB: The action only resolves once all data for the new user has been fetched
 * This can optionally be used to prevent the user from seeing and/or interacting
 * with the POS while the user is only partially switched over
 * (f.ex. logged in as new user, but don't yet have the new user's CAFA)
 */
export const switchUserByPinOrPass = (
  credentials: { pin: string } | { username: string; password: string },
) =>
  async function(dispatch, getState) {
    const clientCode = getClientCode(getState());
    const sessionKey = getSessionKey(getState());
    const currentWarehouseId = getSelectedWarehouseID(getState());
    const [user] = await ('pin' in credentials
      ? API.loginWithPin({ clientCode, sessionKey, cardCode: credentials.pin })
      : API.loginWithPass({
          clientCode,
          username: credentials.username,
          password: credentials.password,
        }));
    // To optimize concurrency, await is not used past this point
    // Instead dependent requests are chained together manually
    //
    // These dependencies are wrapped with Promise.resolve to ensure that we
    // don't depend on whether they are sync or async
    const userRights = fetchUserRightsFromApis(
      { userID: Number(user.userID) },
      {
        clientCode,
        sessionKey: user.sessionKey,
      },
    );
    const cafa = dispatch(
      fetchCafaConfigPerLevel({ level: 'user', level_id: user.userID }),
    );
    // Wait for user rights before switching user
    // Leaving the old rights could allow the new user to perform illegal actions
    // Clearing the old rights would temporarily kick the user back to the login screen while new rights load
    // which would cause a massive wave of data fetches
    const switchUsr = Promise.resolve(userRights).then(userRights => {
      if (!Number(userRights.warehouses?.[currentWarehouseId]?.right ?? 0)) {
        throw new Error(
          i18next.t('alerts:noRightsToLocation', { username: user.userName }),
        );
      }
      return dispatch(
        switchUser({
          user,
          username: user.userName,
          clientCode,
          password:
            'password' in credentials ? credentials.password : undefined,
          userRights,
        }),
      );
    });
    // Fetching sales data only works for the currently-logged-in user.
    // Therefore we must wait for switchUsr to finish first
    const salesData = Promise.resolve(switchUsr).then(() =>
      dispatch(fetchSalesData),
    );

    // All requests scheduled, return a promise indicating when the POS state
    // has been fully updated. The caller may use this to block interactions or
    // show a loading indicator
    await Promise.all([userRights, cafa, switchUsr, salesData]);
  };
